import type { ChatRepository } from '@src/domain/chat/repository/chat.repository';
import { ChatMessage } from '@src/domain/chat/value_objects/chat-message.value-object';
import {
    ChatConnectionStatus,
    // ConnectionStatus
} from '@src/domain/chat/value_objects/connection-status.value-object';

type MessageCallback = (message: ChatMessage) => void;
type StatusCallback = (status: ChatConnectionStatus) => void;
type ErrorCallback = (error: Error) => void;

/**
 * WebSocket Chat Repository Implementation
 * Infrastructure layer - handles WebSocket communication
 */
export class WebSocketChatRepository implements ChatRepository {
    private ws: WebSocket | null = null;
    private messageCallbacks: MessageCallback[] = [];
    private statusCallbacks: StatusCallback[] = [];
    private errorCallbacks: ErrorCallback[] = [];
    // private reconnectAttempts = 0;
    // private readonly maxReconnectAttempts = 3;
    // private readonly reconnectDelay = 10000;
    private currentStatus: ChatConnectionStatus = ChatConnectionStatus.disconnected();
    private readonly wsUrl: string;

    constructor(baseUrl: string) {
        // Convert HTTP URL to WebSocket URL
        const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
        const urlWithoutProtocol = baseUrl.replace(/^https?:\/\//, '');
        const botSecret = import.meta.env.VITE_BOT_SECRET || '';
        this.wsUrl = `${wsProtocol}://${urlWithoutProtocol}/ws/chat?bot_secret=${encodeURIComponent(botSecret)}`;
    }

    async connect(): Promise<void> {
        if (this.ws?.readyState === WebSocket.OPEN) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            this.updateStatus(ChatConnectionStatus.connecting());

            try {
                this.ws = new WebSocket(this.wsUrl);

                this.ws.onopen = () => {
                    // this.reconnectAttempts = 0;
                    this.updateStatus(ChatConnectionStatus.connected());
                    resolve();
                };

                this.ws.onmessage = (event: MessageEvent) => {
                    this.handleMessage(event);
                };

                this.ws.onerror = () => {
                    this.updateStatus(ChatConnectionStatus.error());
                    const error = new Error('Connection error');
                    this.notifyError(error);
                    reject(error);
                };

                this.ws.onclose = () => {
                    this.updateStatus(ChatConnectionStatus.disconnected());
                    // this.attemptReconnect();
                };
            } catch (error) {
                this.updateStatus(ChatConnectionStatus.error());
                const err = error instanceof Error ? error : new Error('Failed to create WebSocket');
                this.notifyError(err);
                reject(err);
            }
        });
    }

    disconnect(): void {
        if (this.ws) {
            this.ws.close(1000, 'Client disconnecting');
            this.ws = null;
            this.updateStatus(ChatConnectionStatus.disconnected());
        }
    }

    async sendMessage(query: string, identifier: string): Promise<void> {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket is not connected');
        }

        const payload = {
            query,
            identifier: identifier,
        };

        this.ws.send(JSON.stringify(payload));
    }

    onMessageReceived(callback: MessageCallback): () => void {
        this.messageCallbacks.push(callback);
        return () => {
            this.messageCallbacks = this.messageCallbacks.filter((cb) => cb !== callback);
        };
    }

    onConnectionStatusChange(callback: StatusCallback): () => void {
        this.statusCallbacks.push(callback);
        // Immediately notify of current status
        callback(this.currentStatus);
        return () => {
            this.statusCallbacks = this.statusCallbacks.filter((cb) => cb !== callback);
        };
    }

    onError(callback: ErrorCallback): () => void {
        this.errorCallbacks.push(callback);
        return () => {
            this.errorCallbacks = this.errorCallbacks.filter((cb) => cb !== callback);
        };
    }

    getConnectionStatus(): ChatConnectionStatus {
        return this.currentStatus;
    }

    private handleMessage(event: MessageEvent): void {
        try {
            const response: any = JSON.parse(event.data);

            // Handle backward compatibility: if new format, use most_relevant/least_relevant
            // If old format, treat pascos as most_relevant
            const mostRelevantPascos = response.most_relevant || response.pascos || [];
            const leastRelevantPascos = response.least_relevant || [];

            // Create domain message
            const message = ChatMessage.create({
                id: Date.now().toString(),
                role: 'assistant',
                content: response.message,
                timestamp: new Date(),
                mostRelevantPascos,
                leastRelevantPascos,
            });

            // Notify all subscribers
            this.messageCallbacks.forEach((callback) => {
                callback(message);
            });
        } catch (error) {
            const err = error instanceof Error ? error : new Error('Failed to parse message');
            this.notifyError(err);
        }
    }

    private updateStatus(status: ChatConnectionStatus): void {
        this.currentStatus = status;
        this.statusCallbacks.forEach((callback) => callback(status));
    }

    private notifyError(error: Error): void {
        this.errorCallbacks.forEach((callback) => callback(error));
    }

    // private attemptReconnect(): void {
    //     if (this.reconnectAttempts < this.maxReconnectAttempts) {
    //         this.reconnectAttempts++;
    //         this.updateStatus(ChatConnectionStatus.create(ConnectionStatus.RECONNECTING));

    //         const delay = this.reconnectDelay * this.reconnectAttempts;
    //         setTimeout(() => {
    //             this.connect().catch((error) => {
    //                 console.error('Reconnection failed:', error);
    //             });
    //         }, delay);
    //     }
    // }
}
